import {type Room} from 'matrix-js-sdk'

export type MatrixRoomSummary = {
  roomId: string
  name: string
  topic?: string
  avatarUrl?: string
  lastMessage?: string
  lastMessageTs?: number
  lastMessageIsMe?: boolean
  unreadCount: number
  memberCount: number
  isDirect: boolean
  /** DIDs of other members (extracted from Matrix user IDs) */
  otherMemberDids: string[]
}

/** Convert Matrix localpart back to DID: did_plc_abc123 -> did:plc:abc123 */
export function localpartToDid(localpart: string): string | null {
  // Match pattern: did_plc_xxxxx or did_web_xxxxx
  const match = localpart.match(/^(did)_([a-z]+)_(.+)$/)
  if (!match) return null
  return `${match[1]}:${match[2]}:${match[3]}`
}

/** Extract the other members' DIDs from a Matrix room */
export function extractOtherMemberDids(room: Room, myUserId: string): string[] {
  const allMembers = room.currentState.getMembers().filter(m => {
    const membership = m.membership
    return membership === 'join' || membership === 'invite'
  })

  const dids: string[] = []
  for (const member of allMembers) {
    if (member.userId === myUserId) continue
    const localpartMatch = member.userId.match(/^@([^:]+):/)
    if (localpartMatch) {
      const did = localpartToDid(localpartMatch[1])
      if (did) dids.push(did)
    }
  }
  return dids
}

/** Extract a DID from a Matrix user ID like @did_plc_abc123:server */
function matrixUserIdToDid(matrixUserId: string): string | null {
  const localpartMatch = matrixUserId.match(/^@([^:]+):/)
  if (!localpartMatch) return null
  return localpartToDid(localpartMatch[1])
}

function attachmentPreviewText(msgtype: string | undefined): string | null {
  switch (msgtype) {
    case 'm.image':
      return 'Sent a photo'
    case 'm.video':
      return 'Sent a video'
    case 'm.audio':
      return 'Sent a voice message'
    case 'm.file':
      return 'Sent a file'
    default:
      return null
  }
}

export function roomToSummary(
  room: Room,
  myUserId: string,
  checkIsDirectRoom: (roomId: string) => boolean,
  getDirectRoomUserId?: (roomId: string) => string | null,
): MatrixRoomSummary {
  const lastEvent = room.getLastLiveEvent()
  const allMembers = room.currentState.getMembers().filter(m => {
    const membership = m.membership
    return membership === 'join' || membership === 'invite'
  })

  let otherMemberDids = extractOtherMemberDids(room, myUserId)
  const isDirect = checkIsDirectRoom(room.roomId)

  // Fallback: if no other members found from room state, use m.direct data
  if (otherMemberDids.length === 0 && isDirect && getDirectRoomUserId) {
    const directUserId = getDirectRoomUserId(room.roomId)
    if (directUserId) {
      const did = matrixUserIdToDid(directUserId)
      if (did) otherMemberDids = [did]
    }
  }

  return {
    roomId: room.roomId,
    name: room.name || 'Unnamed Chat',
    topic: room.currentState.getStateEvents('m.room.topic', '')?.getContent()
      ?.topic,
    avatarUrl:
      room.getAvatarUrl(room.client.getHomeserverUrl(), 40, 40, 'crop') ||
      undefined,
    lastMessage:
      lastEvent?.getType() === 'm.room.message'
        ? attachmentPreviewText(lastEvent.getContent()?.msgtype as string) ||
          (lastEvent.getContent()?.body as string) ||
          undefined
        : undefined,
    lastMessageTs: lastEvent?.getTs(),
    lastMessageIsMe:
      lastEvent?.getType() === 'm.room.message'
        ? lastEvent.getSender() === myUserId
        : undefined,
    unreadCount: room.getUnreadNotificationCount() || 0,
    memberCount: allMembers.length,
    isDirect,
    otherMemberDids,
  }
}
